import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import { acceptInvite, lookupInvite, LookupInviteResponse } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';

/**
 * Single-page invite acceptance:
 *   1. URL: investors.nurev.io/accept-invite?token=<32-byte b64url>
 *   2. On mount: GET /auth/accept-invite/:token → show "Welcome, {name}"
 *   3. User sets password (8+ chars), confirms, submits
 *   4. POST /auth/accept-invite → JWT issued, session set, redirect to /
 */
export function AcceptInviteScreen() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const nav = useNavigate();
  const setSession = useAuthStore(s => s.setSession);

  const [invite, setInvite] = useState<LookupInviteResponse | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLookupError('No invite token in the URL.');
      return;
    }
    lookupInvite(token)
      .then(setInvite)
      .catch(err => {
        const msg =
          (err as { response?: { data?: { message?: string }; status?: number } })
            ?.response?.data?.message ||
          ((err as { response?: { status?: number } })?.response?.status === 410
            ? 'This invite link has expired. Email contact@nurev.io for a new one.'
            : 'This invite link is invalid.');
        setLookupError(msg);
      });
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords don\'t match.');
      return;
    }
    setSubmitting(true);
    try {
      const { token: jwt, user } = await acceptInvite({ token, password });
      setSession(jwt, {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role
      });
      nav('/', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Could not accept invite. Try again or contact support.';
      setSubmitError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-nurev-bg px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700">
            <TrendingUp className="h-5 w-5 text-white" />
          </span>
          <div>
            <p className="text-base font-bold leading-tight text-nurev-text">
              NUREV
            </p>
            <p className="text-xs leading-tight text-nurev-muted">
              Investor Portal
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-nurev-border bg-nurev-surface p-7">
          {lookupError ? (
            <>
              <h1 className="mb-1 text-xl font-bold text-nurev-text">
                Invite link issue
              </h1>
              <p className="text-sm text-red-300">{lookupError}</p>
            </>
          ) : !invite ? (
            <>
              <h1 className="mb-1 text-xl font-bold text-nurev-text">
                Validating invite…
              </h1>
              <p className="text-sm text-nurev-muted">One moment.</p>
            </>
          ) : (
            <>
              <h1 className="mb-1 text-xl font-bold text-nurev-text">
                Welcome, {invite.name.split(' ')[0]}.
              </h1>
              <p className="mb-6 text-sm text-nurev-muted">
                Set a password for{' '}
                <span className="font-mono text-nurev-text-soft">
                  {invite.email}
                </span>
                {' '}to access your shareholder portal.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
                    New password (8+ chars)
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-wider text-nurev-muted">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2.5 text-sm text-nurev-text focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>

                {submitError && (
                  <p className="text-xs text-red-300">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !password || !confirm}
                  className="w-full rounded-lg bg-emerald-500 py-2.5 text-sm font-medium text-nurev-bg transition hover:bg-emerald-400 disabled:opacity-40"
                >
                  {submitting ? 'Accepting…' : 'Accept & sign in'}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[10px] text-nurev-muted">
          Need help? Email{' '}
          <a
            href="mailto:contact@nurev.io"
            className="text-emerald-300 hover:text-emerald-200"
          >
            contact@nurev.io
          </a>
        </p>
      </div>
    </div>
  );
}
